import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePeople } from '@/hooks/useConsoleData';
import { usePhone } from '@/hooks/usePhone';
import { PeopleCards } from './PeopleCards';
import { PersonChips } from './PeopleChips';
import {
  DOOR_LABEL,
  LAST_SEEN_BUCKETS,
  LAST_SEEN_LABEL,
  PLANS,
  PLAN_LABEL,
  PLATFORMS,
  PLATFORM_LABEL,
  SORT_LABEL,
  STAGES,
  STAGE_LABEL,
  STUCK_REASONS,
  STUCK_REASON_LABEL,
  asksLine,
  attemptsLine,
  elinaLine,
  filterByStuck,
  stuckWords,
  type PeopleQuery,
  type PersonRow,
  type SortKey,
  type StuckReason,
} from '@/lib/people-data';
import { NOT_MEASURED, ago, day, num } from '@/lib/format';
import s from '@/pages/console/console.module.css';

/**
 * Everyone, one row each: stage, plan, last seen, streak, checks, Active
 * Mode minutes, platforms, what Elina knows, open asks, and the counts of
 * what they wrote us and told us (chips, nothing at zero). Filters by stage,
 * plan, platform, last-seen bucket and door; sorted by last seen, stage,
 * checks or signup; paged. The row's name opens the person. A value the
 * server could not measure prints the words for it.
 *
 * Under 640px the rows are cards (PeopleCards) and the facet filters live
 * in a bottom sheet behind one Filters button; the search stays in the row.
 *
 * The Stuck column is the first milestone not reached and, for a person
 * short of a first check, why (pv-auth reads it from the client's attempt
 * reports or PostHog for the page it returns). The stuck-reason filter is
 * applied here to the page's rows, since the server has no such filter;
 * the pager says so.
 */
const FACET_FILTERS: { key: 'stage' | 'plan' | 'platform' | 'lastSeen' | 'door'; label: string; values: readonly string[]; labelOf: (v: string) => string }[] = [
  { key: 'stage', label: 'Stage', values: STAGES, labelOf: (v) => STAGE_LABEL[v as keyof typeof STAGE_LABEL] ?? v },
  { key: 'plan', label: 'Plan', values: PLANS, labelOf: (v) => PLAN_LABEL[v as keyof typeof PLAN_LABEL] ?? v },
  { key: 'platform', label: 'Platform', values: PLATFORMS, labelOf: (v) => PLATFORM_LABEL[v as keyof typeof PLATFORM_LABEL] ?? v },
  { key: 'lastSeen', label: 'Last seen', values: LAST_SEEN_BUCKETS, labelOf: (v) => LAST_SEEN_LABEL[v as keyof typeof LAST_SEEN_LABEL] ?? v },
  { key: 'door', label: 'Door', values: Object.keys(DOOR_LABEL), labelOf: (v) => DOOR_LABEL[v] ?? v },
];

const COLUMNS: { key: string; label: string; sort?: SortKey }[] = [
  { key: 'who', label: 'Who' },
  { key: 'stage', label: 'Stage', sort: 'stage' },
  { key: 'stuck', label: 'Stuck before, why' },
  { key: 'plan', label: 'Plan' },
  { key: 'lastSeen', label: 'Last seen', sort: 'last_seen' },
  { key: 'streak', label: 'Streak' },
  { key: 'checks', label: 'Checks', sort: 'checks' },
  { key: 'active', label: 'Active Mode 7 d / total' },
  { key: 'platforms', label: 'Platforms' },
  { key: 'elina', label: 'Elina knows' },
  { key: 'asks', label: 'Asks' },
  { key: 'wrote', label: 'Wrote, told us' },
  { key: 'signup', label: 'Signed up', sort: 'signup' },
  { key: 'source', label: 'Source' },
  { key: 'links', label: '' },
];

export function PeopleTable({ query, onChange }: { query: PeopleQuery; onChange: (next: PeopleQuery) => void }) {
  const list = usePeople(query);
  const d = list.data;
  const phone = usePhone();
  const [sheet, setSheet] = useState(false);
  const set = (patch: Partial<PeopleQuery>) => onChange({ ...query, ...patch, page: patch.page ?? 1 });
  const sortBy = (key: SortKey) => set({ sort: key, dir: query.sort === key && query.dir === 'desc' ? 'asc' : 'desc' });
  const pages = d ? Math.max(1, Math.ceil(d.total / d.pageSize)) : 1;
  const facetsOn = FACET_FILTERS.filter((f) => query[f.key]).length + (query.stuck ? 1 : 0);
  const anyFilter = !!(query.stage || query.plan || query.platform || query.lastSeen || query.door || query.q || query.stuck);
  // The stuck filter is browser-side over the page; the counts in its options are of this page's rows.
  const rows = useMemo(() => (d ? filterByStuck(d.people, query.stuck) : []), [d, query.stuck]);
  const stuckCounts = useMemo(() => {
    const out: Partial<Record<StuckReason, number>> = {};
    for (const v of STUCK_REASONS) { const n = filterByStuck(d?.people ?? [], v).length; if (n) out[v] = n; }
    return out;
  }, [d]);
  const clear = () => onChange({ sort: query.sort, dir: query.dir, includeQa: query.includeQa });

  const facetSelects = FACET_FILTERS.map((f) => (
    <select key={f.key} className={s.selectInline} aria-label={f.label} value={(query[f.key] as string | null) ?? ''} onChange={(e) => set({ [f.key]: e.target.value || null } as Partial<PeopleQuery>)}>
      <option value="">{f.label}: all</option>
      {f.values.map((v) => (
        <option key={v} value={v}>
          {f.labelOf(v)}{d ? ` (${d.facets[f.key][v] ?? 0})` : ''}
        </option>
      ))}
    </select>
  ));
  const stuckSelect = (
    <select key="stuck" className={s.selectInline} aria-label="Stuck reason" value={query.stuck ?? ''} onChange={(e) => onChange({ ...query, stuck: (e.target.value || null) as StuckReason | null })} title="Why they stopped before the first check, on this page's rows">
      <option value="">Stuck reason: all</option>
      {STUCK_REASONS.map((v) => (
        <option key={v} value={v}>{STUCK_REASON_LABEL[v]}{d ? ` (${stuckCounts[v] ?? 0} on this page)` : ''}</option>
      ))}
    </select>
  );
  const qaToggle = (
    <label className={s.sub} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input type="checkbox" checked={!!query.includeQa} onChange={(e) => set({ includeQa: e.target.checked })} /> show QA accounts
    </label>
  );
  const search = <input className={s.search} value={query.q ?? ''} onChange={(e) => set({ q: e.target.value || null })} placeholder="Search name, email, phone" aria-label="Search people" />;

  return (
    <div className={s.stack} data-panel="people-table">
      {phone ? (
        <div className={s.filters}>
          {search}
          <button type="button" className={s.btnGhost} onClick={() => setSheet(true)} aria-haspopup="dialog" aria-expanded={sheet}>Filters{facetsOn ? ` (${facetsOn})` : ''}</button>
          {anyFilter && <button type="button" className={s.linkBtn} onClick={clear}>Clear</button>}
          {sheet && (
            <div className={s.sheetBackdrop} onClick={() => setSheet(false)}>
              <div className={s.sheet} role="dialog" aria-label="Filters" onClick={(e) => e.stopPropagation()}>
                <div className={s.sheetHead}>
                  <span className={s.sheetTitle}>Filters</span>
                  <button type="button" className={s.btn} onClick={() => setSheet(false)}>Done</button>
                </div>
                {facetSelects}
                {stuckSelect}
                <label className={s.sheetLabel}>Sort by
                  <select className={s.selectInline} aria-label="Sort" value={query.sort ?? 'last_seen'} onChange={(e) => set({ sort: e.target.value as SortKey })}>
                    {(Object.keys(SORT_LABEL) as SortKey[]).map((k) => <option key={k} value={k}>{SORT_LABEL[k]}</option>)}
                  </select>
                </label>
                {qaToggle}
                {anyFilter && <button type="button" className={s.linkBtn} onClick={() => { clear(); setSheet(false); }}>Clear filters</button>}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className={s.filters}>
          {search}
          {facetSelects}
          {stuckSelect}
          {qaToggle}
          {anyFilter && <button type="button" className={s.linkBtn} onClick={clear}>Clear filters</button>}
        </div>
      )}

      {list.isError && <div className={s.error}>Could not load the people. This page needs GET /provider/admin/people.</div>}
      {d && d.truncated && <div className={s.notice}>More than the first 5000 accounts exist; the oldest are not in this list.</div>}
      {d && d.unavailable.length > 0 && <div className={s.jWarn}>Not read this time: {d.unavailable.map((u) => u.source).join(', ')}. Those columns say "not measured" rather than 0.</div>}

      {query.stuck && d && <div className={s.notice}>Stuck reason "{STUCK_REASON_LABEL[query.stuck]}" is matched on this page's {num(d.people.length)} rows only: the server reads the reason per page. Raise rows per page to see more.</div>}
      {phone && d && <PeopleCards people={rows} />}
      {!phone && <div className={s.tableWrap}>
        <table className={`${s.table} ${s.peopleTable}`}>
          <thead>
            <tr>
              {COLUMNS.map((c) => (
                <th key={c.key}>
                  {c.sort ? (
                    <button type="button" className={`${s.sortBtn} ${query.sort === c.sort ? s.sortOn : ''}`} onClick={() => sortBy(c.sort!)} aria-sort={query.sort === c.sort ? (query.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      {c.label}{query.sort === c.sort ? (query.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                    </button>
                  ) : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d && rows.length === 0 && <tr><td colSpan={COLUMNS.length}>Nobody matches these filters.</td></tr>}
            {rows.map((p) => (
              <tr key={p.userId} data-user={p.userId}>
                <td className="wrap">
                  <Link to={`/people/${p.userId}`} className={s.rowLink}>{p.name || p.email}</Link>
                  <div className={s.sub}>{p.email}{p.phone ? ` · ${p.phone}` : ''}{p.qa ? ' · QA account' : ''}{!p.verified ? ' · not verified' : ''}</div>
                </td>
                <td><span className={`${s.pill} ${p.stage === 'paid' ? s.pill_paid : s.pill_stage}`}>{STAGE_LABEL[p.stage]}</span></td>
                <td className="wrap"><StuckCell p={p} /></td>
                <td>{PLAN_LABEL[p.plan]}{p.planDetail.trialEndsAt && p.plan === 'trial' ? <div className={s.sub}>ends {day(p.planDetail.trialEndsAt)}</div> : null}</td>
                <td>{p.lastSeenAt ? ago(p.lastSeenAt) : 'never'}{p.lastSeenSource && <div className={s.sub}>{p.lastSeenSource.replace(/_/g, ' ')}</div>}</td>
                <td>{p.streak === null ? <span className={s.missing}>no checks</span> : num(p.streak)}</td>
                <td>{num(p.checks)}{p.checkDays !== p.checks ? <div className={s.sub}>{num(p.checkDays)} days</div> : null}{p.lastScore !== null && <div className={s.sub}>last {Math.round(p.lastScore)}</div>}</td>
                <td>{p.activeMinutes7d === null && p.activeMinutesTotal === null ? <span className={s.missing}>{p.activeSessions ? 'no timed session' : NOT_MEASURED}</span> : `${p.activeMinutes7d ?? 0} / ${p.activeMinutesTotal ?? 0} min`}{p.activeSessions ? <div className={s.sub}>{num(p.activeSessions)} sessions</div> : null}</td>
                <td>{p.platforms.length ? p.platforms.map((x) => PLATFORM_LABEL[x]).join(', ') : <span className={s.missing}>none recorded</span>}</td>
                <td className="wrap"><span className={s.sub}>{elinaLine(p)}</span></td>
                <td>{asksLine(p) ? <span className={s.usageAsk}>{asksLine(p)}</span> : ''}</td>
                <td className="wrap"><PersonChips p={p} /></td>
                <td>{day(p.signupAt)}</td>
                <td className="wrap"><span className={s.sub}>{p.source.label}</span></td>
                <td>{p.ghlUrl ? <a href={p.ghlUrl} target="_blank" rel="noreferrer">GHL</a> : <span className={s.missing}>no GHL contact</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>}

      {d && (
        <div className={s.pager}>
          <span>{num(d.total)} of {num(d.totalAll)} people{d.total !== d.totalAll ? ' match' : ''}{query.stuck ? ` · ${num(rows.length)} of this page's ${num(d.people.length)} with that stuck reason` : ''} · sorted by {SORT_LABEL[d.sort.key]}</span>
          <button type="button" className={s.btnGhost} disabled={d.page <= 1} onClick={() => set({ page: d.page - 1 })}>Previous</button>
          <span>page {d.page} of {pages}</span>
          <button type="button" className={s.btnGhost} disabled={d.page >= pages} onClick={() => set({ page: d.page + 1 })}>Next</button>
          <select className={s.selectInline} aria-label="Rows per page" value={d.pageSize} onChange={(e) => set({ pageSize: Number(e.target.value) })}>
            {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n} per page</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

/** The first milestone not reached and, short of a first check, why, with the attempt count under it. */
function StuckCell({ p }: { p: PersonRow }) {
  const why = stuckWords(p);
  const tries = attemptsLine(p.attempts);
  if (!why) {
    return (
      <div className={s.stuckCell}>
        <span className={s.sub}>{p.stuckStep ? `before ${p.stuckStepLabel}` : 'all milestones reached'}</span>
        {tries ? <span className={s.sub}>{tries}</span> : null}
      </div>
    );
  }
  return (
    <div className={s.stuckCell} data-stuck={p.stuckReason ?? 'none'}>
      <span className={s.sub}>before {p.stuckStepLabel ?? 'first check'}</span>
      <span className={why.measured ? '' : s.missing}>{why.text}</span>
      {tries ? <span className={s.sub}>{tries}</span> : null}
    </div>
  );
}
